import Button from '@jetbrains/ring-ui/components/button/button'
import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import {memo} from 'react'
import type {PreloadedQuery} from 'react-relay/relay-hooks/EntryPointTypes'

import BranchFilter from '../../../../containers/BranchFilter'
import OverviewBreadcrumbs from '../../../../containers/OverviewBreadcrumbs'
import useDocumentTitle from '../../../../hooks/useDocumentTitle'
import useStatusSubscription from '../../../../hooks/useStatusSubscription'
import {PlaceIdList} from '../../../../plugins/plugins.types'
import {Policy} from '../../../../rest/branches'
import {getBuildType} from '../../../../selectors'
import type {BuildTypeId, ProjectId} from '../../../../types'
import type {BranchSelectBranchesQuery} from '../../../common/BranchSelect/__generated__/BranchSelectBranchesQuery.graphql'
import BreadcrumbsDropdown from '../../../common/BreadcrumbsDropdown/BreadcrumbsDropdown'
import BuildTypeActionsDropdown from '../../../common/BuildTypeActionsDropdown/BuildTypeActionsDropdown'
import BuildTypePauseInfo from '../../../common/BuildTypePauseInfo/BuildTypePauseInfo'
import Description from '../../../common/Description/Description.container'
import EditEntity from '../../../common/EditEntity/EditEntity.container'
import FavoriteStar from '../../../common/FavoriteStar/FavoriteStar.container'
import HealthItems from '../../../common/HealthItems/HealthItems'
import BuildTypeInvestigations from '../../../common/Investigations/BuildTypeInvestigations'
import Plugin from '../../../common/Plugin/Plugin'
import OverviewHeaderIcon from '../../../common/ProjectOrBuildTypeIcon/OverviewHeaderIcon.container'
import RunBuildInSelectedBranch from '../../../common/RunBuild/RunBuildInSelectedBranch.container'
import SequenceLoader from '../../../common/SequenceLoader/SequenceLoader'

import OverviewSystemProblems from './OverviewSystemProblems/OverviewHeaderSystemProblems.container'

import styles from './BuildTypePageHeader.css'

import {useAppSelector} from 'src/hooks/react-redux'

const icon = <OverviewHeaderIcon withArrow size="M" />

type Props = {
  className?: string
  buildTypeId: BuildTypeId
  projectId: ProjectId | null | undefined
  branchesQueryRef: PreloadedQuery<BranchSelectBranchesQuery>
}
function BuildTypePageHeader({className, buildTypeId, projectId, branchesQueryRef}: Props) {
  const {name, paused} = useAppSelector(state => getBuildType(state, buildTypeId)) ?? {}
  const headingClassNames = classNames(styles.heading, className)

  useStatusSubscription({buildTypeId, projectId})
  useDocumentTitle(name != null ? `${name} — TeamCity` : null)

  return (
    <div
      data-test="overview-header"
      data-project-id={projectId}
      data-buildtype-id={buildTypeId}
      className={styles.header}
    >
      <div className={styles.links}>
        <OverviewBreadcrumbs
          className={styles.breadcrumbs}
          linkClassName={styles.breadcrumbsLink}
        />
        <SequenceLoader showFirstLoadingItem className={styles.linksSequenceLoader} loader="">
          <RunBuildInSelectedBranch
            seqKey="RunBuildInSelectedBranch"
            buildTypeId={buildTypeId}
            withVisibilityDetect={false}
            isPrimary
          />
          <EditEntity seqKey="EditEntity" />
        </SequenceLoader>
      </div>
      <H1 className={headingClassNames}>
        <BreadcrumbsDropdown buildTypeId={buildTypeId} icon={icon} />
        <span>{name}</span>
        <FavoriteStar className={styles.star} buildTypeId={buildTypeId} />
      </H1>
      <Description className={styles.description} lines={3} buildTypeId={buildTypeId} />
      <OverviewSystemProblems className={styles.systemProblems} />
      {Boolean(paused) && (
        <BuildTypePauseInfo className={styles.buildTypePauseInfo} buildTypeId={buildTypeId} />
      )}
      <SequenceLoader className={styles.subHeader} itemClassName={styles.subHeaderItem} loader="">
        <BranchFilter
          seqKey="branchFilter"
          className={styles.branchFilter}
          branchesQueryRef={branchesQueryRef}
          policy={Policy.ALL_BRANCHES}
        />
        <BuildTypeActionsDropdown
          seqKey="actions"
          className={styles.actions}
          anchor={<Button dropdown>{`Actions`}</Button>}
        />
        <BuildTypeInvestigations
          seqKey="investigations"
          className={styles.investigation}
          buildTypeId={buildTypeId}
        />
        <HealthItems seqKey="healthItems" className={styles.healthItems} />
      </SequenceLoader>
      <Plugin placeId={PlaceIdList.SAKURA_BEFORE_CONTENT} />
    </div>
  )
}
export default memo(BuildTypePageHeader)
